import {
  findGdrPDA,
  findGemBoxPDA,
  findRarityPDA,
  findVaultAuthorityPDA,
  findWhitelistProofPDA,
  GEM_BANK_PROG_ID,
  GemBankClient,
  isKp,
  WhitelistType,
} from "@gemworks/gem-farm-ts";
import { findEditionPda, Metaplex } from "@metaplex-foundation/js";
import {
  AuthorizationData,
  Metadata,
} from "@metaplex-foundation/mpl-token-metadata";
import { programs } from "@metaplex/js";
import { BN } from "@project-serum/anchor";
import {
  ASSOCIATED_TOKEN_PROGRAM_ID,
  TOKEN_PROGRAM_ID,
} from "@solana/spl-token";
import { SignerWalletAdapter } from "@solana/wallet-adapter-base";
import {
  ComputeBudgetProgram,
  Connection,
  Keypair,
  PublicKey,
  SystemProgram,
  SYSVAR_INSTRUCTIONS_PUBKEY,
  SYSVAR_RENT_PUBKEY,
} from "@solana/web3.js";
import {
  AUTHORIZATION_RULES_PROGRAM_ID,
  TOKEN_METADATA_PROGRAM_ID,
} from "../../../constants";

//need a separate func coz fetching IDL is async and can't be done in constructor
export async function initGemBank(
  conn: Connection,
  wallet: SignerWalletAdapter
) {
  const walletToUse = wallet;
  const idl = await (await fetch("gem_bank.json")).json();
  return new GemBank(conn, walletToUse as any, idl);
}

export const getTotalComputeIxs = (
  compute: number,
  priorityMicroLamports = 1
) => {
  const modifyComputeUnits = ComputeBudgetProgram.setComputeUnitLimit({
    units: compute,
  });
  const addPriorityFee = ComputeBudgetProgram.setComputeUnitPrice({
    microLamports: priorityMicroLamports,
  });
  return [modifyComputeUnits, addPriorityFee];
};

export const fetchNft = async (conn: Connection, mint: PublicKey) => {
  const mplex = new Metaplex(conn);
  return await mplex
    .nfts()
    .findByMint({ mintAddress: mint, loadJsonMetadata: true });
};

export const findTokenRecordPDA = async (mint: PublicKey, token: PublicKey) => {
  return PublicKey.findProgramAddress(
    [
      Buffer.from("metadata"),
      TOKEN_METADATA_PROGRAM_ID.toBuffer(),
      mint.toBuffer(),
      Buffer.from("token_record"),
      token.toBuffer(),
    ],
    TOKEN_METADATA_PROGRAM_ID
  );
};

export class GemBank extends GemBankClient {
  constructor(conn: Connection, wallet: any, idl: any) {
    super(conn, wallet, idl, GEM_BANK_PROG_ID);
  }

  async initBankWallet() {
    const bank = Keypair.generate();
    const txSig = await this.initBank(
      bank,
      this.wallet.publicKey,
      this.wallet.publicKey
    );
    return { bank, txSig };
  }

  async initVaultWallet(bank: PublicKey) {
    return this.initVault(
      bank,
      this.wallet.publicKey,
      this.wallet.publicKey,
      this.wallet.publicKey,
      "test_vault"
    );
  }

  async setVaultLockWallet(
    bank: PublicKey,
    vault: PublicKey,
    vaultLocked: boolean
  ) {
    return this.setVaultLock(bank, vault, this.wallet.publicKey, vaultLocked);
  }

  async depositGemWallet(
    bank: PublicKey,
    vault: PublicKey,
    gemAmount: BN,
    gemMint: PublicKey,
    gemSource: PublicKey,
    creator: PublicKey
  ) {
    const [mintProof] = await findWhitelistProofPDA(bank, gemMint);
    const [creatorProof] = await findWhitelistProofPDA(bank, creator);
    const metadata = await programs.metadata.Metadata.getPDA(gemMint);

    return this.buildDepositGemPnft(
      bank,
      vault,
      this.wallet.publicKey,
      gemAmount,
      gemMint,
      gemSource,
      mintProof,
      // metadata,
      creatorProof
    );
  }

  async myPrepPnftAccounts({
    nftMetadata,
    nftMint,
    sourceAta,
    destAta,
    authData = null,
  }: {
    nftMetadata?: PublicKey;
    nftMint: PublicKey;
    sourceAta: PublicKey;
    destAta: PublicKey;
    authData?: AuthorizationData | null;
  }) {
    let meta;
    let creators: PublicKey[] = [];
    if (nftMetadata) {
      meta = nftMetadata;
    } else {
      const nft = await (
        await fetchNft(this.provider.connection, nftMint)
      ).run();
      meta = nft.metadataAddress;
      creators = nft.creators.map((c) => c.address);
    }

    const inflatedMeta = await Metadata.fromAccountAddress(
      this.provider.connection,
      meta
    );
    const ruleSet = inflatedMeta.programmableConfig?.ruleSet;

    const [ownerTokenRecordPda, ownerTokenRecordBump] =
      await findTokenRecordPDA(nftMint, sourceAta);
    const [destTokenRecordPda, destTokenRecordBump] = await findTokenRecordPDA(
      nftMint,
      destAta
    );

    //retrieve edition PDA
    const nftEditionPda = findEditionPda(nftMint);

    //have to re-serialize due to anchor limitations
    const authDataSerialized = authData
      ? {
          payload: Object.entries(authData.payload.map).map(([k, v]) => {
            return { name: k, payload: v };
          }),
        }
      : null;

    return {
      meta,
      creators,
      ownerTokenRecordBump,
      ownerTokenRecordPda,
      destTokenRecordBump,
      destTokenRecordPda,
      ruleSet,
      nftEditionPda,
      authDataSerialized,
    };
  }

  async buildWithdrawGemPnft(
    bank: PublicKey,
    vault: PublicKey,
    vaultOwner: PublicKey | Keypair,
    gemAmount: BN,
    gemMint: PublicKey,
    receiver: PublicKey,
    compute = 400000,
    priorityFee = 1
  ) {
    const [gemBox, gemBoxBump] = await findGemBoxPDA(vault, gemMint);
    const [GDR, GDRBump] = await findGdrPDA(vault, gemMint);
    const [vaultAuth, vaultAuthBump] = await findVaultAuthorityPDA(vault);
    const [gemRarity, gemRarityBump] = await findRarityPDA(bank, gemMint);

    const gemDestination = await this.findATA(gemMint, receiver);

    const signers = [];
    if (isKp(vaultOwner)) signers.push(<Keypair>vaultOwner);

    //pnft
    const {
      meta,
      ownerTokenRecordBump,
      ownerTokenRecordPda,
      destTokenRecordBump,
      destTokenRecordPda,
      ruleSet,
      nftEditionPda,
      authDataSerialized,
    } = await this.myPrepPnftAccounts({
      nftMint: gemMint,
      destAta: gemDestination,
      authData: null, //currently useless
      sourceAta: gemBox,
    });
    const remainingAccounts = [];
    if (!!ruleSet) {
      remainingAccounts.push({
        pubkey: ruleSet,
        isSigner: false,
        isWritable: false,
      });
    }

    console.log(
      `withdrawing ${gemAmount} gems from ${gemBox.toBase58()}, GDR ${GDR.toBase58()} (PNFT)`
    );
    const builder = this.bankProgram.methods
      .withdrawGemPnft(
        vaultAuthBump,
        gemBoxBump,
        GDRBump,
        gemRarityBump,
        gemAmount,
        authDataSerialized,
        !!ruleSet
      )
      .accounts({
        bank,
        vault,
        owner: isKp(vaultOwner)
          ? (<Keypair>vaultOwner).publicKey
          : (vaultOwner as PublicKey),
        authority: vaultAuth,
        gemBox,
        gemDepositReceipt: GDR,
        gemDestination,
        gemMint,
        gemRarity,
        receiver,
        tokenProgram: TOKEN_PROGRAM_ID,
        associatedTokenProgram: ASSOCIATED_TOKEN_PROGRAM_ID,
        systemProgram: SystemProgram.programId,
        rent: SYSVAR_RENT_PUBKEY,
        gemMetadata: meta,
        gemEdition: nftEditionPda,
        destTokenRecord: destTokenRecordPda,
        ownerTokenRecord: ownerTokenRecordPda,
        pnftShared: {
          authorizationRulesProgram: AUTHORIZATION_RULES_PROGRAM_ID,
          tokenMetadataProgram: TOKEN_METADATA_PROGRAM_ID,
          instructions: SYSVAR_INSTRUCTIONS_PUBKEY,
        },
      })
      .signers(signers)
      .remainingAccounts(remainingAccounts);

    const [modifyComputeUnits, addPriorityFee] = getTotalComputeIxs(
      compute,
      priorityFee
    );

    const ixs = [
      modifyComputeUnits,
      addPriorityFee,
      await builder.instruction(),
    ];

    return {
      gemBox,
      gemBoxBump,
      GDR,
      GDRBump,
      gemRarity,
      gemRarityBump,
      vaultAuth,
      vaultAuthBump,
      gemDestination,
      builder,
      ixs,
      ownerTokenRecordBump,
      ownerTokenRecordPda,
      destTokenRecordBump,
      destTokenRecordPda,
      meta,
    };
  }

  async withdrawGemWallet(
    wallet: { publicKey: PublicKey },
    bank: PublicKey,
    vault: PublicKey,
    gemAmount: BN,
    gemMint: PublicKey
  ) {
    return this.buildWithdrawGemPnft(
      bank,
      vault,
      wallet.publicKey,
      gemAmount,
      gemMint,
      wallet.publicKey
    );
  }

  async addToWhitelistWallet(
    bank: PublicKey,
    addressToWhitelist: PublicKey,
    whitelistType: WhitelistType
  ) {
    return this.addToWhitelist(
      bank,
      this.wallet.publicKey,
      addressToWhitelist,
      whitelistType
    );
  }

  async removeFromWhitelistWallet(bank: PublicKey, addressToRemove: PublicKey) {
    return this.removeFromWhitelist(
      bank,
      this.wallet.publicKey,
      addressToRemove
    );
  }
}
